/**
 * usePendingDocuments — as origens do card "Pendências" da home.
 *
 * Espelha `useRecentDocuments` nas decisões estruturais, pelas MESMAS razões
 * (leia o cabeçalho de lá antes de mexer aqui):
 *
 * 1. **Os orçamentos entram por PARÂMETRO.** A home já lê `quotes` inteiro para
 *    o hero e para os "Últimos documentos"; buscar de novo seria uma terceira
 *    consulta à mesma tabela — e abriria a janela em que o card conta um
 *    orçamento que o resto da tela ainda não conhece.
 * 2. **O FINANCEIRO entra por PARÂMETRO** pelo mesmo motivo: quem já o carregou
 *    é o saldo do hero (`useFinanceEntriesRaw`). E é lá que mora o gate de
 *    papel — aqui a origem chega `null` quando o usuário não pode ler
 *    `finance_entries`, e `null` OMITE o grupo em vez de mostrar zero (#38).
 * 3. **`initialDataUpdatedAt: 0` é obrigatório.** Esta query nasce DESABILITADA
 *    (`user`/`activeCompany` resolvem depois da montagem), então
 *    `refetchOnMount` já passou quando ela habilita; sem o carimbo o cache
 *    semeado conta como FRESCO e a PRIMEIRA busca nunca sai — uma OS aberta em
 *    outro aparelho ficaria invisível, sem erro nenhum na tela (#158).
 * 4. **`safe()` em volta de cada promessa.** Sem rede o cliente do Supabase
 *    REJEITA (não devolve `{ error }`), e uma rejeição dentro de `Promise.all`
 *    descarta as outras respostas: o card inteiro sumiria por causa de uma
 *    tabela (#58).
 *
 * ─── O QUE É PRÓPRIO DAQUI ─────────────────────────────────────────────────
 *
 * **A consulta pede SÓ o que está pendente** (`.in('status', …)`), com projeção
 * mínima. É o oposto do `useServiceOrders`, que traz `*, items(*), photos(*)` —
 * carregar isso na home só para contar linhas seria pagar o módulo inteiro por
 * um número. Como o recorte é o mesmo dos dois lados, o predicado do cliente
 * (`buildPendingSummary`) e o filtro do servidor nascem da MESMA lista de
 * status exportada por `pendingDocuments.ts`: divergir faria o card contar uma
 * coisa online e outra offline, sem erro (#93).
 *
 * **Sem teto de linhas, de propósito.** O conjunto é pequeno por natureza (são
 * as pendências, não o histórico) e um `.limit()` aqui seria um corte
 * SILENCIOSO no número que o card afirma — o defeito que o #50 descreve.
 *
 * **NFS-e devolve `null` quando a leitura falha**, e não `[]`: o módulo fiscal
 * é online-only (sem cache offline, mesmo contrato do `useWork`), então sem
 * rede a linha de notas simplesmente não aparece, nunca zerada.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { useCompany } from '@/contexts/useCompany';
import { supabase } from '@/integrations/supabase/client';
import { getCachedContracts, getCachedServiceOrders } from '@/lib/offlineDataCache';
import {
  buildPendingSummary,
  PENDING_CONTRACT_STATUSES,
  PENDING_NFSE_STATUSES,
  PENDING_SERVICE_ORDER_STATUSES,
  type PendingSummary,
} from '@/lib/pendingDocuments';
import type { FinanceEntryWithStatus } from '@/types/database';

const PENDING_DOCUMENTS_KEY = 'pending-documents';

/** Projeções mínimas: o card conta linhas, não mostra documento. */
const ORDER_COLUMNS = 'id, status';
const CONTRACT_COLUMNS = 'id, status';
const NFSE_COLUMNS = 'id, status';

interface Pool {
  serviceOrders: any[];
  contracts: any[];
  /** `null` = não deu para ler (sem rede) — ver o cabeçalho. */
  nfseDocuments: any[] | null;
}

const EMPTY_POOL: Pool = { serviceOrders: [], contracts: [], nfseDocuments: null };

/** Uma criação offline ainda não sincronizada carrega id temporário (§6). */
const isOfflineRow = (row: any): boolean => String(row?.id ?? '').startsWith('offline_');

/**
 * O que o cache local já sabe, recortado pelo MESMO predicado do servidor.
 *
 * Filtrar aqui (em vez de devolver a lista inteira e deixar o
 * `buildPendingSummary` filtrar) não é redundância: é o que faz a semente ter a
 * mesma forma da resposta do servidor, para a tela não mudar de número quando a
 * busca chega.
 */
function poolFromCache(userId: string): Pool {
  const orders = getCachedServiceOrders(userId) ?? [];
  const contracts = getCachedContracts(userId) ?? [];
  return {
    serviceOrders: orders.filter((o: any) =>
      (PENDING_SERVICE_ORDER_STATUSES as readonly string[]).includes(String(o?.status ?? '')),
    ),
    contracts: contracts.filter((c: any) =>
      (PENDING_CONTRACT_STATUSES as readonly string[]).includes(String(c?.status ?? '')),
    ),
    nfseDocuments: null,
  };
}

/**
 * Junta o que o servidor devolveu com as criações offline que ainda estão na
 * fila. Sem isto, a OS aberta sem rede DESAPARECE da contagem assim que o sinal
 * volta e a primeira busca responde.
 */
function mergePending(serverRows: any[], cachedRows: any[]): any[] {
  const pending = cachedRows.filter(isOfflineRow);
  if (pending.length === 0) return serverRows;
  const seen = new Set(serverRows.map((row) => String(row?.id)));
  return [...serverRows, ...pending.filter((row) => !seen.has(String(row?.id)))];
}

export interface UsePendingDocumentsResult {
  summary: PendingSummary;
  isLoading: boolean;
}

/**
 * @param quotes Os orçamentos já carregados pela home (inclui vendas de balcão).
 * @param financeEntries Lançamentos já carregados, ou `null` quando o papel do
 *   usuário não permite lê-los.
 */
export function usePendingDocuments(
  quotes: any[],
  financeEntries: FinanceEntryWithStatus[] | null,
): UsePendingDocumentsResult {
  const { user } = useAuth();
  const { activeCompany } = useCompany();

  const userId = user?.id ?? null;
  const companyId = activeCompany?.id ?? null;

  const query = useQuery({
    queryKey: [PENDING_DOCUMENTS_KEY, companyId],
    enabled: Boolean(userId && companyId),
    networkMode: 'offlineFirst',
    staleTime: 30 * 1000,
    refetchOnMount: 'always',
    // Ver a decisão 3 no cabeçalho — sem isto a primeira busca nunca acontece.
    initialDataUpdatedAt: 0,
    initialData: (): Pool | undefined => (userId ? poolFromCache(userId) : undefined),
    queryFn: async (): Promise<Pool> => {
      if (!userId || !companyId) return EMPTY_POOL;

      const safe = <T,>(p: PromiseLike<T>): Promise<T | { data: null; error: true }> =>
        Promise.resolve(p).then(
          (r) => r,
          () => ({ data: null, error: true as const }),
        );

      const scoped = (table: string, columns: string, statuses: readonly string[]) =>
        supabase
          .from(table as any)
          .select(columns)
          .eq('company_id', companyId)
          .in('status', statuses as string[]);

      const [orders, contracts, nfse] = await Promise.all([
        safe(scoped('service_orders', ORDER_COLUMNS, PENDING_SERVICE_ORDER_STATUSES)),
        safe(scoped('contracts', CONTRACT_COLUMNS, PENDING_CONTRACT_STATUSES)),
        // Exclusão de nota é LÓGICA (`discarded_at`), nunca física.
        safe(scoped('nfse_documents', NFSE_COLUMNS, PENDING_NFSE_STATUSES).is('discarded_at', null)),
      ]);

      const cached = poolFromCache(userId);
      /** Origem que falhou cai no cache — nunca vira "não há pendência". */
      const rowsOf = (result: any, fallback: any[]): any[] =>
        result?.error || !result?.data ? fallback : (result.data as any[]);

      return {
        serviceOrders: mergePending(rowsOf(orders, cached.serviceOrders), cached.serviceOrders),
        contracts: mergePending(rowsOf(contracts, cached.contracts), cached.contracts),
        // Sem cache offline: a leitura que falha vira `null`, e o grupo some.
        nfseDocuments: (nfse as any)?.error || !(nfse as any)?.data ? null : ((nfse as any).data as any[]),
      };
    },
  });

  const pool = query.data ?? EMPTY_POOL;
  const summary = useMemo(
    () =>
      buildPendingSummary({
        quotes,
        serviceOrders: pool.serviceOrders,
        contracts: pool.contracts,
        nfseDocuments: pool.nfseDocuments,
        financeEntries,
      }),
    [quotes, pool.serviceOrders, pool.contracts, pool.nfseDocuments, financeEntries],
  );

  return { summary, isLoading: query.isLoading };
}
